import type { ScrollToBottom } from 'use-stick-to-bottom';
import { PinnedMessage, usePinnedMessageToggleState } from '~/components/chat/PinnedMessage';
import { LegacyButton } from '~/components/ui/LegacyButton';
import { ButtonLink } from '~/components/ui/Link';
import { supabaseStore, type ConnectData } from '~/lib/stores/supabase';

interface SupabaseStopgapButtonProps {
  isLast: boolean;
  messageBoxHeight: number;
  change: ConnectData;
  scrollToBottom: ScrollToBottom;
  onStickyHeight: (height: number) => void;
  discardSupabaseChange: (changeId: string, removeMessage?: boolean) => Promise<void>;
}

export function SupabaseStopgapButton({
  change,
  messageBoxHeight,
  isLast,
  scrollToBottom,
  onStickyHeight,
  discardSupabaseChange,
}: SupabaseStopgapButtonProps) {
  const { setToggled, toggled } = usePinnedMessageToggleState({ scrollToBottom, isLast });

  const discard = () => {
    discardSupabaseChange(change.id, true);
  };

  return (
    <PinnedMessage
      setToggled={setToggled}
      toggled={toggled}
      sticky={false} // to avoid showing this message on every message
      title={
        <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
          <div className="i-logos:supabase-icon text-lg" />
          <span>Need a new Supabase connection?</span>
        </div>
      }
      actions={
        <div className="flex flex-col gap-4 justify-between">
          <p>
            Looks like you need to connect a Supabase database.{' '}
            <ButtonLink onClick={supabaseStore.openSupabaseProjectConfiguration}>Click here to connect one</ButtonLink>.
            If you already have one, or we made an error assuming you needed one, you can safely ignore this message.
          </p>
          <div className="flex gap-2.5">
            <LegacyButton variant="warn" size="sm" onClick={discard}>
              Discard
            </LegacyButton>
            <a href="https://supabase.com/docs" target="_blank" className="flex items-center gap-1 ml-auto">
              <div className="i-ph:arrow-square-out text-lg" />
              Docs
            </a>
          </div>
        </div>
      }
      messageBoxHeight={messageBoxHeight}
      messageClassName="before:border-brands-supabase-accent before:border-l-3"
      isLast={isLast}
      scrollToBottom={scrollToBottom}
      onStickyHeight={onStickyHeight}
    />
  );
}
